/* Silent decorative loops; retry playback without showing native controls. */
(function () {
  'use strict';
  if (window.deramicVideoPlaybackInitialized) return;
  window.deramicVideoPlaybackInitialized = true;

  var selector = 'video[data-deramic-video]';
  var states = new Map();
  var supportsObserver = typeof window.IntersectionObserver === 'function';
  var loadObserver;
  var playObserver;

  function shouldPlay(state) {
    return !state.disposed && state.visible && !document.hidden;
  }

  function silent(video) {
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.controls = false;
  }

  function hasSource(video) {
    if (video.currentSrc || (video.getAttribute('src') || '').trim()) return true;
    var sources = video.querySelectorAll('source[src]');
    for (var i = 0; i < sources.length; i++) {
      if ((sources[i].getAttribute('src') || '').trim()) return true;
    }
    return false;
  }

  function prepare(state) {
    if (state.loaded || state.disposed || document.hidden || !hasSource(state.video)) return;
    silent(state.video);
    state.video.autoplay = shouldPlay(state);
    state.loaded = true;
    state.video.preload = 'auto';
    state.video.load();
    if (loadObserver) loadObserver.unobserve(state.video);
  }

  function retryWhenReady(state) {
    if (!state.denied || state.readyRetried || state.pending || !shouldPlay(state)) return;
    state.readyRetried = true;
    state.denied = false;
    state.blocked = false;
    sync(state);
  }

  function sync(state) {
    var video = state.video;
    silent(video);
    var active = shouldPlay(state);
    video.autoplay = active;
    if (active !== state.active) {
      state.active = active;
      if (active) {
        state.cycle++;
        state.abortRetried = false;
        state.readyRetried = false;
        state.denied = false;
        state.blocked = false;
      }
    }
    if (!active) {
      video.pause();
      return;
    }
    prepare(state);
    if (state.pending || state.blocked || !video.paused || !hasSource(video)) return;
    state.pending = true;
    var attemptCycle = state.cycle;
    var readyAtAttempt = state.readyVersion;
    function started() {
      state.pending = false;
      state.denied = false;
      state.blocked = false;
      if (!shouldPlay(state)) {
        video.autoplay = false;
        video.pause();
      }
    }
    function failed(error) {
      state.pending = false;
      if (state.disposed) return;
      state.blocked = true;
      state.denied = !!(error && error.name === 'NotAllowedError');
      // An old attempt may reject after a real exit/re-entry or tab return.
      if (shouldPlay(state) && attemptCycle !== state.cycle) {
        state.blocked = false;
        state.denied = false;
        sync(state);
      } else if (error && error.name === 'AbortError' && shouldPlay(state) && !state.abortRetried) {
        state.abortRetried = true;
        state.blocked = false;
        sync(state);
      } else if (state.readyVersion > readyAtAttempt) {
        // Readiness may arrive while the initial play promise is still pending.
        retryWhenReady(state);
      }
    }
    try {
      silent(video);
      var result = video.play();
      if (result && typeof result.then === 'function') result.then(started, failed);
      else started();
    } catch (error) {
      failed(error);
    }
  }

  if (supportsObserver) {
    loadObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var state = states.get(entry.target);
        if (!state) return;
        state.near = entry.isIntersecting;
        if (state.near) prepare(state);
      });
    }, { rootMargin: '250px 0px', threshold: 0 });
    playObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var state = states.get(entry.target);
        if (!state) return;
        state.visible = entry.isIntersecting && entry.intersectionRatio >= 0.1;
        sync(state);
      });
    }, { threshold: 0.1 });
  }

  function register(video) {
    if (states.has(video)) return;
    var lazy = video.getAttribute('data-deramic-video') === 'lazy';
    var state = { video: video, loaded: !lazy, visible: !supportsObserver,
      near: false, pending: false, disposed: false, active: false, cycle: 0,
      blocked: false, denied: false, abortRetried: false, readyRetried: false, readyVersion: 0 };
    states.set(video, state);
    silent(video);
    video.setAttribute('aria-hidden', 'true');
    // Once managed, native autoplay must not restart an offscreen/hidden loop.
    video.autoplay = false;
    state.onReady = function () {
      if (state.disposed) return;
      state.readyVersion++;
      retryWhenReady(state);
    };
    video.addEventListener('loadeddata', state.onReady);
    video.addEventListener('canplay', state.onReady);
    if (supportsObserver) {
      if (lazy) loadObserver.observe(video);
      video.pause();
      playObserver.observe(video);
    } else {
      // Older browsers still get silent automatic loops, without a play button.
      sync(state);
    }
  }

  function init(root) {
    if (root.matches && root.matches(selector)) register(root);
    root.querySelectorAll(selector).forEach(register);
  }

  function unload(root) {
    states.forEach(function (state, video) {
      if (root !== video && !root.contains(video)) return;
      state.disposed = true;
      video.autoplay = false;
      video.pause();
      video.removeEventListener('loadeddata', state.onReady);
      video.removeEventListener('canplay', state.onReady);
      if (loadObserver) loadObserver.unobserve(video);
      if (playObserver) playObserver.unobserve(video);
      states.delete(video);
    });
  }

  function retryVisible(state) {
    if (!shouldPlay(state)) return;
    state.blocked = false;
    state.denied = false;
    sync(state);
  }

  document.addEventListener('visibilitychange', function () {
    states.forEach(function (state) {
      if (state.near) prepare(state);
      sync(state);
    });
  });
  ['touchstart', 'touchend', 'pointerdown', 'click', 'keydown'].forEach(function (name) {
    document.addEventListener(name, function (event) {
      if (!event.isTrusted) return;
      states.forEach(retryVisible);
    }, { capture: true, passive: true });
  });
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  document.addEventListener('shopify:section:unload', function (event) { unload(event.target); });
  window.addEventListener('pageshow', function () {
    states.forEach(function (state) {
      retryVisible(state);
      sync(state);
    });
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); }, { once: true });
  } else {
    init(document);
  }
})();
